import { toast } from "@/components/ui/Toast";
import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { Encounter } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams, useRouter } from "@tanstack/react-router";

type StartAsyncEncounterResponse = {
  encounter: Encounter;
};

type StartAsyncEncounterError = {
  detail: string;
};

export const useStartAsyncEncounter = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { navigate } = useRouter();
  const location = useLocation();
  const queryClient = useQueryClient();

  return useApiMutation<
    StartAsyncEncounterResponse,
    void,
    StartAsyncEncounterError
  >({
    mutationOptions: {
      mutationKey: ["start-async-encounter", patientId as string],
      mutationFn: async () => {
        const response = await axiosInstance.post("/new-api/encounter/async", {
          patientId: patientId as string,
        });
        return response.data;
      },
      onSuccess: (data) => {
        queryClient.refetchQueries({
          queryKey: QueryKeys.Provider.PATIENT_TABS,
        });

        navigate({
          to: location.pathname,
          params: { patientId: patientId as string },
          search: {
            encounterId: data.encounter.id as string,
            chatTab: "async" as const,
          },
        });
      },
      onError: (error) => {
        toast.error(
          `Error starting async encounter: ${error.response?.data?.detail}`,
        );
      },
    },
  });
};
